import { SupplierContext } from '@shared/components/suppliers/SupplierContext';
import { produce } from 'immer';
import defaultTo from 'lodash/defaultTo';
import isString from 'lodash/isString';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import type { SuppliersModel } from '../../../../../Shared/js/@types/SuppliersModel';
import { RootState } from '../../../../../Shared/js/redux/rootReducer';
import type { SearchCriteria } from '../../../../js/@types/SearchCriteria';
import type { SearchModel } from '../../../../js/@types/SearchModel';
import { actions as searchActions } from '../../../../js/redux/searchSlice';
import BroadbandActions from '../../actions/BroadbandActions';
import ConnectionFilter from './main/ConnectionFilter';
import DataFilter from './main/DataFilter';
import ErrorPanel from './main/ErrorPanel';
import ShareButton from './main/ShareButton';
import ShowFiltersButton from './main/ShowFiltersButton';
import SortMenu from './main/SortMenu';
import SpeedFilter from './main/SpeedFilter';
import SuppliersFilter from './main/SuppliersFilter';
import FilterModal from './modal/FilterModal';
import { SearchFilterContext } from './SearchFilterContext';

const ModalOpenEventName = 'Filters.OpenModal';

const mapStateToProps = (state: RootState, ownProps: { modal?: RootState['broadband']['modal'] }) => {
    return produce(ownProps, draft => {
        const productAreaState = state.broadband;
        draft.modal = defaultTo(productAreaState?.modal, ownProps.modal);
    });
};

const mapDispatchToProps = {
    updateCriteria: searchActions.updateCriteria,
    updateData: searchActions.updateData,
    updateSpeed: searchActions.updateSpeed,
    updateSuppliers: searchActions.updateSuppliers,
    updateSort: searchActions.updateSort
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export interface Props extends PropsFromRedux {
    criteria: SearchCriteria;
    legacyActions?: BroadbandActions;

    filtersData: SearchModel['filters'];
    allConnectionsSelected?: boolean | null;
    hideSupplierFilter: boolean;
    hideConnectionFilter: boolean;
    isSortByOnlyVisible: boolean;
    showMaxUpfrontSlider: boolean;
    suppliers: SuppliersModel;
    sortMenu?: {
        currentSortDescription: string;
        currentSortExpression: string;
        currentSortAscending: boolean;
        hideSort: boolean;
        showEmailSearchButton: boolean;
        sortExpressions: Array<{ expression: string; description: string; isAscending: boolean }>;
    };
    unavailableConnectionTypes: Array<{ shortUrl: string; id?: number }>;
    connectionTypesToIgnore?: string[];
    showModal: (tabName?: string) => void;
    showShareButton?: boolean;
    shareButtonResources?: {
        resultsWidget: string;
        popularProvidersWidget: string;
    };
    /** When set (e.g. "100%" on Search page), overrides SpeedDataSelect width; otherwise decider logic uses screenSize elsewhere. */
    dataWidth?: string;
}

interface ParsedSortExpression {
    expression?: string;
    Expression?: string;
    description?: string;
    Description?: string;
    isAscending?: boolean;
    IsAscending?: boolean;
}

const Filters = (props: Props) => {
    const [sortMenuData, setSortMenuData] = useState<Props['sortMenu']>(props.sortMenu);
    const [shareButtonData, setShareButtonData] = useState<{
        showShareButton: boolean;
        resultsWidget: string;
        popularProvidersWidget: string;
    } | null>(null);

    // Read SortMenu from data attribute when SearchResults loads
    useEffect(() => {
        const readSortMenuFromDataAttribute = () => {
            const searchResultsPartial = document.getElementById('search-results-partial');
            if (searchResultsPartial) {
                const sortMenuAttr = searchResultsPartial.getAttribute('data-sort-menu');
                if (sortMenuAttr) {
                    try {
                        const parsed = JSON.parse(sortMenuAttr);
                        // Map the parsed data to match our interface (handle both PascalCase and camelCase)
                        const mapped: Props['sortMenu'] = {
                            currentSortDescription:
                                parsed.currentSortDescription || parsed.CurrentSortDescription || '',
                            currentSortExpression: parsed.currentSortExpression || parsed.CurrentSortExpression || '',
                            currentSortAscending:
                                parsed.currentSortAscending !== undefined
                                    ? parsed.currentSortAscending
                                    : parsed.CurrentSortAscending !== undefined
                                      ? parsed.CurrentSortAscending
                                      : false,
                            hideSort: parsed.hideSort !== undefined ? parsed.hideSort : parsed.HideSort || false,
                            showEmailSearchButton:
                                parsed.showEmailSearchButton !== undefined
                                    ? parsed.showEmailSearchButton
                                    : parsed.ShowEmailSearchButton || false,
                            sortExpressions: (parsed.sortExpressions || parsed.SortExpressions || []).map(
                                (se: ParsedSortExpression) => ({
                                    expression: se.expression || se.Expression || '',
                                    description: se.description || se.Description || '',
                                    isAscending:
                                        se.isAscending !== undefined
                                            ? se.isAscending
                                            : se.IsAscending !== undefined
                                              ? se.IsAscending
                                              : false
                                })
                            )
                        };
                        setSortMenuData(prev => {
                            if (JSON.stringify(prev) === JSON.stringify(mapped)) {
                                return prev; // No change, return previous state
                            }
                            return mapped;
                        });
                    } catch (e) {
                        console.error('Failed to parse sortMenu data:', e);
                    }
                }
            }
        };

        const readShareButtonFromDataAttribute = () => {
            const searchResultsPartial = document.getElementById('search-results-partial');
            if (searchResultsPartial) {
                const shareButtonAttr = searchResultsPartial.getAttribute('data-share-button');
                if (shareButtonAttr) {
                    try {
                        const parsed = JSON.parse(shareButtonAttr);
                        const mapped = {
                            showShareButton:
                                parsed.showShareButton !== undefined
                                    ? parsed.showShareButton
                                    : parsed.ShowShareButton || false,
                            resultsWidget: parsed.resultsWidget || parsed.ResultsWidget || '',
                            popularProvidersWidget: parsed.popularProvidersWidget || parsed.PopularProvidersWidget || ''
                        };
                        setShareButtonData(prev => {
                            if (JSON.stringify(prev) === JSON.stringify(mapped)) {
                                return prev;
                            }
                            return mapped;
                        });
                    } catch (e) {
                        console.error('Failed to parse shareButton data:', e);
                    }
                }
            }
        };

        readSortMenuFromDataAttribute();
        readShareButtonFromDataAttribute();

        const resultsContainer = document.getElementById('results-container');
        if (!resultsContainer) {
            return;
        }

        const observer = new MutationObserver(mutations => {
            // Check if search-results-partial was added or its attributes changed
            let shouldRead = false;

            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    // Check if search-results-partial was added
                    mutation.addedNodes.forEach(node => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element;
                            if (
                                element.id === 'search-results-partial' ||
                                element.querySelector('#search-results-partial')
                            ) {
                                shouldRead = true;
                            }
                        }
                    });
                } else if (mutation.type === 'attributes') {
                    // Check if it's the data-sort-menu attribute on search-results-partial
                    const target = mutation.target as Element;
                    if (
                        target.id === 'search-results-partial' &&
                        (mutation.attributeName === 'data-sort-menu' || mutation.attributeName === 'data-share-button')
                    ) {
                        shouldRead = true;
                    }
                }
            }

            if (shouldRead) {
                // Small delay to ensure DOM is fully updated
                setTimeout(() => {
                    readSortMenuFromDataAttribute();
                    readShareButtonFromDataAttribute();
                }, 0);
            }
        });

        // Observe the results container for child changes and subtree attribute changes
        observer.observe(resultsContainer, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-sort-menu', 'data-share-button']
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    const handleShowModal = useCallback(
        (tabName?: string | void | unknown) => {
            if (isString(tabName)) {
                props.showModal(tabName);
            } else {
                props.showModal();
            }
        },
        [props]
    );

    const handleOnMount = useCallback(() => {
        wo$('body').on(ModalOpenEventName, handleShowModal);

        // Unmount
        return () => {
            wo$('body').off(ModalOpenEventName, handleShowModal);
        };
    }, [handleShowModal]);

    const handleShowAllProviders = useCallback(() => {
        handleShowModal('providers');
    }, [handleShowModal]);

    const handleRetry = useCallback(() => {
        props.legacyActions.actions.searchRetry();
    }, [props.legacyActions.actions]);

    const handleModalCriteriaChanged = useCallback(
        (criteria: SearchCriteria) => {
            props.updateCriteria(criteria);
        },
        [props]
    );

    const selectedSuppliers = props.criteria.common.suppliers;

    // TODO: Do not use properties with the same name as existing built-in functions, i.e. Array.values()
    // HACK: To avoid confusion between Array.values() and our custom property with the same name
    const selectedSupplierKeys = Array.isArray(selectedSuppliers) ? selectedSuppliers : selectedSuppliers?.values;

    const isBusinessOnly =
        props.criteria.common.customerType && props.criteria.common.customerType.toLowerCase() === 'business';

    const getSupplierKey = useCallback(item => item.shortUrl, []);

    const handleSortChange = useCallback(
        (expression: string, isAscending: boolean) => {
            props.updateSort({ expression, isAscending });
        },
        [props]
    );

    useEffect(() => handleOnMount(), [handleOnMount]);

    return (
        <React.Fragment>
            <SearchFilterContext.Provider value={{ data: props.filtersData, criteria: props.criteria }}>
                <SupplierContext.Provider value={{ suppliers: props.suppliers }}>
                    <FilterModal
                        key="filters"
                        {...props.filtersData}
                        criteria={props.criteria}
                        onCriteriaChanged={handleModalCriteriaChanged}
                        hideSupplierFilter={props.hideSupplierFilter}
                        showMaxUpfrontSlider={props.showMaxUpfrontSlider}
                        unavailableConnectionTypes={props.unavailableConnectionTypes}
                        connectionTypesToIgnore={props.connectionTypesToIgnore}
                    />

                    <div
                        id="filters-original"
                        className="pad-y-3-xs pad-y-5-sm pad-y-5-md pad-y-5-lg sep-b-1 bor-b-2"
                        data-hide-filters={props.isSortByOnlyVisible}
                        data-hide-connection={props.hideConnectionFilter}
                    >
                        <div data-filter-item="providers">
                            <SuppliersFilter
                                suppliers={props.suppliers}
                                selectedKeys={selectedSupplierKeys}
                                isBusinessOnly={isBusinessOnly}
                                showHeader={true}
                                showTextOnly={props.hideSupplierFilter}
                                textContent={props.filtersData.defaultSupplierAvailableText}
                                onChanged={props.updateSuppliers}
                                onShowAll={handleShowAllProviders}
                                getKey={getSupplierKey}
                            />
                        </div>
                        <div
                            data-filter-item="separator"
                            className="bor-t-2 bor-t-0-xs bor-t-0-sm mar-y-4-xs mar-y-2-sm mar-t-6-md mar-b-4-md mar-t-6-lg mar-b-4-lg"
                        ></div>
                        <div data-filter-item="speed">
                            <SpeedFilter
                                {...props.filtersData.speedSlider}
                                current={props.criteria.speed}
                                maxSpeed={props.criteria.maxSpeed}
                                onChanged={props.updateSpeed}
                                dataWidth={props.dataWidth}
                            />
                        </div>
                        <div data-filter-item="data">
                            <DataFilter
                                {...props.filtersData.dataSlider}
                                current={props.criteria.data}
                                onChanged={props.updateData}
                                dataWidth={props.dataWidth}
                            />
                        </div>
                        <div data-filter-item="connection">
                            <ConnectionFilter
                                allConnectionTypes={props.filtersData.allConnectionTypes}
                                allConnectionsSelected={props.allConnectionsSelected}
                                unavailableConnectionTypes={props.unavailableConnectionTypes}
                                connectionTypesToIgnore={props.connectionTypesToIgnore}
                                connectionTypeCategories={props.filtersData.connectionTypeCategories}
                                current={props.criteria.connectionTypes}
                                onChanged={props.legacyActions.actions.updateConnectionTypes}
                                tabToConnectionTypesMapping={props.filtersData.tabToConnectionTypesMapping}
                                tab={props.criteria.common.tab}
                            />
                        </div>
                        {props.filtersData.showAllFilters ? (
                            <div data-filter-item="more-filters">
                                <ShowFiltersButton
                                    title={props.filtersData.resources.filters}
                                    onClick={handleShowModal}
                                />
                            </div>
                        ) : null}
                        {sortMenuData && !sortMenuData.hideSort && (
                            <div data-filter-item="sort-by" className="display-flex flex-gap-3">
                                <SortMenu
                                    currentSortDescription={sortMenuData.currentSortDescription}
                                    currentSortExpression={sortMenuData.currentSortExpression}
                                    currentSortAscending={sortMenuData.currentSortAscending}
                                    hideSort={sortMenuData.hideSort}
                                    showEmailSearchButton={sortMenuData.showEmailSearchButton}
                                    sortExpressions={sortMenuData.sortExpressions}
                                    onSortChange={handleSortChange}
                                />

                                {shareButtonData && shareButtonData.showShareButton && (
                                    <ShareButton
                                        showShareButton={shareButtonData.showShareButton}
                                        resultsWidgetLabel={shareButtonData.resultsWidget}
                                        popularProvidersWidgetLabel={shareButtonData.popularProvidersWidget}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <ErrorPanel
                        title={props.filtersData.resources.errorUpdatingResults}
                        description={props.filtersData.resources.pleaseTryAgain}
                        retryLabel={props.filtersData.resources.retry}
                        onRetry={handleRetry}
                    />
                </SupplierContext.Provider>
            </SearchFilterContext.Provider>
        </React.Fragment>
    );
};

export default connector(Filters);
